// A task's links (blocks, relates to, duplicate of, spawned from) in the task
// drawer: list both directions with readable labels, add one by ticket number
// or task id, remove one. Blocking links are what the board shows as "⛓ n".

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import {
  LINK_CYCLE_CODE,
  TASK_LINK_KINDS,
  type Task,
  type TaskLinkKind,
  type TaskLinkView,
} from '@goblic/shared'
import { ApiError, tasksApi } from '../../lib/api-client'
import { useWorkspaceId } from '../../lib/auth'
import { Notice } from '../ui'

/** The link as read from the current task's side ('blocks', 'blocked by', ...). */
export function linkLabel(link: Pick<TaskLinkView, 'kind' | 'direction'>): string {
  const outgoing = link.direction === 'outgoing'
  switch (link.kind) {
    case 'blocks':
      return outgoing ? 'blocks' : 'blocked by'
    case 'relates':
      return 'relates to'
    case 'duplicate_of':
      return outgoing ? 'duplicate of' : 'duplicated by'
    case 'spawned_from':
      return outgoing ? 'spawned from' : 'spawned'
  }
}

const KIND_CHOICES: Record<TaskLinkKind, string> = {
  blocks: 'blocks',
  relates: 'relates to',
  duplicate_of: 'is a duplicate of',
  spawned_from: 'was spawned from',
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Opens another task's drawer on the page you're on: the same path with
 * `?taskId=` swapped (the tasks page and the board both open the drawer from it).
 */
export function useOpenTask(): (taskId: string) => void {
  const router = useRouter()
  return (taskId) => {
    const { pathname, searchStr } = router.state.location
    const params = new URLSearchParams(searchStr)
    params.set('taskId', taskId)
    params.delete('tab')
    router.history.push(`${pathname}?${params.toString()}`)
  }
}

function errorText(error: unknown): string {
  if (error instanceof ApiError && error.code === LINK_CYCLE_CODE)
    return 'That would make the blocking links circular.'
  return error instanceof Error ? error.message : 'Couldn’t change the link.'
}

export function TaskLinks({ task }: { task: Task }) {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const openTask = useOpenTask()
  const [adding, setAdding] = useState(false)
  const [kind, setKind] = useState<TaskLinkKind>('blocks')
  const [target, setTarget] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const linksQuery = useQuery({
    queryKey: ['task', task.id, 'links'],
    queryFn: () => tasksApi.links(task.id),
  })
  const links = linksQuery.data?.links ?? []

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['task', task.id, 'links'] })
    // The other task's links and the boards' blocker counts change too.
    void queryClient.invalidateQueries({ queryKey: ['task'] })
    void queryClient.invalidateQueries({ queryKey: ['project', task.projectId] })
  }

  /** Ticket number ('190', 'GOB-190') within the project, or a task id. */
  async function resolveTarget(raw: string): Promise<string> {
    const text = raw.trim()
    if (UUID_RE.test(text)) return text
    const ticket = /^(?:[A-Za-z][A-Za-z0-9]*-)?(\d+)$/.exec(text)
    if (!ticket) throw new Error('Enter a ticket number such as 190 or GOB-190, or a task id.')
    const found = await tasksApi.list({
      workspaceId,
      projectId: task.projectId,
      ticketNo: Number(ticket[1]),
    })
    const match = found.items[0]
    if (!match) throw new Error(`No task with ticket number ${ticket[1]} in this project.`)
    return match.id
  }

  const addMutation = useMutation({
    mutationFn: async () => {
      const otherId = await resolveTarget(target)
      return tasksApi.addLink(task.id, { kind, taskId: otherId })
    },
    onSuccess: () => {
      refresh()
      setTarget('')
      setAdding(false)
      setFormError(null)
    },
    onError: (error) => setFormError(errorText(error)),
  })

  const removeMutation = useMutation({
    // The link is stored on the task it points away from.
    mutationFn: (link: TaskLinkView) =>
      link.direction === 'outgoing'
        ? tasksApi.removeLink(task.id, link.kind, link.task.id)
        : tasksApi.removeLink(link.task.id, link.kind, task.id),
    onSuccess: refresh,
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    setFormError(null)
    if (target.trim()) addMutation.mutate()
  }

  return (
    <section>
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-sm font-medium text-ink-3">Links</h3>
        <button type="button" className="btn-secondary btn-sm" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add link'}
        </button>
      </div>

      {adding ? (
        <form
          className="mb-3 space-y-3 rounded-control border border-line-strong p-3"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-wrap items-center gap-2 text-base text-ink-2">
            <span>This task</span>
            <select
              aria-label="Link kind"
              className="field-input w-auto"
              value={kind}
              onChange={(event) => setKind(event.target.value as TaskLinkKind)}
            >
              {TASK_LINK_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_CHOICES[k]}
                </option>
              ))}
            </select>
            <input
              aria-label="Ticket number or task id"
              className="field-input min-w-[10rem] flex-1"
              placeholder="Ticket number (190) or task id"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              required
            />
          </div>
          {formError ? <Notice tone="error">{formError}</Notice> : null}
          <div className="flex justify-end">
            <button type="submit" className="btn-primary btn-sm" disabled={addMutation.isPending}>
              {addMutation.isPending ? 'Linking…' : 'Link'}
            </button>
          </div>
        </form>
      ) : null}

      {linksQuery.isLoading ? (
        <p className="text-sm text-ink-3">Loading links…</p>
      ) : links.length === 0 ? (
        <p className="text-base text-ink-3">No links.</p>
      ) : (
        <ul className="space-y-1.5">
          {links.map((link) => (
            <li
              key={`${link.kind}-${link.direction}-${link.task.id}`}
              className="flex items-baseline gap-2 text-base"
            >
              <span className="w-28 shrink-0 text-sm text-ink-3">{linkLabel(link)}</span>
              <button
                type="button"
                className="link min-w-0 truncate text-left"
                onClick={() => openTask(link.task.id)}
              >
                {link.task.ticketKey ? (
                  <span className="mr-1.5 font-mono text-sm">{link.task.ticketKey}</span>
                ) : null}
                {link.task.title}
              </button>
              <span className="shrink-0 text-sm text-ink-3">{link.task.status.replace(/_/g, ' ')}</span>
              <button
                type="button"
                className="btn-ghost btn-sm ml-auto shrink-0"
                aria-label={`Remove link to ${link.task.ticketKey ?? link.task.title}`}
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate(link)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {removeMutation.isError ? (
        <div className="mt-2">
          <Notice tone="error">{errorText(removeMutation.error)}</Notice>
        </div>
      ) : null}
    </section>
  )
}
